/**
 * A picture turned in its frame (mood boards, Huanrui 2026-10-16)
 * -------------------------------------------------------------
 * Any angle, in degrees clockwise. The turn is split into quarter turns and
 * what is left (-45 to 45 degrees). The quarter turns turn the picture whole;
 * what is left straightens it the way a photo editor does: turned, and then
 * the largest rectangle of the picture's own shape that fits inside it, so no
 * corner of the frame is ever empty. That rectangle is "the picture turned":
 * the frame's crop is a crop of it, and every cut -- for Miro, for a frame on
 * a project's board, for the picture of the whole sheet -- is made from it.
 */

/** 0 up to 360. */
export const normalAngle = (turn: number | undefined): number => {
  const n = (turn ?? 0) % 360
  return Math.round((n < 0 ? n + 360 : n) * 10) / 10
}

/** Quarter turns (0 to 3) and the rest (-45 to 45 degrees). */
export function splitTurn(turn: number | undefined): { q: number; rest: number } {
  const n = normalAngle(turn)
  const steps = Math.round(n / 90)
  return { q: steps % 4, rest: Math.round((n - steps * 90) * 10) / 10 }
}

export const isTurned = (turn: number | undefined): boolean => normalAngle(turn) !== 0

/** Width over height once turned: the quarter turns swap it, the rest keeps it. */
export const turnedRatio = (ratio: number, turn: number | undefined): number =>
  splitTurn(turn).q % 2 === 1 ? 1 / (ratio || 1) : ratio

/**
 * How much of a w x h picture turned by `rest` degrees is kept: the largest
 * rectangle of the same shape that fits inside it, as a share of its size.
 */
export function keptShare(w: number, h: number, rest: number): number {
  const a = (Math.abs(rest) * Math.PI) / 180
  if (a < 1e-6) return 1
  const c = Math.cos(a)
  const s = Math.sin(a)
  return Math.min(w / (w * c + h * s), h / (w * s + h * c))
}

/** The picture turned, ready to be drawn from; itself when it is not turned. */
export function turnedSource(
  bitmap: ImageBitmap,
  turn: number | undefined,
): { source: CanvasImageSource; width: number; height: number } {
  const { q, rest } = splitTurn(turn)
  if (q === 0 && rest === 0) return { source: bitmap, width: bitmap.width, height: bitmap.height }
  const sideways = q % 2 === 1
  const w = sideways ? bitmap.height : bitmap.width
  const h = sideways ? bitmap.width : bitmap.height
  const keep = keptShare(w, h, rest)
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(w * keep))
  canvas.height = Math.max(1, Math.round(h * keep))
  const pen = canvas.getContext('2d')
  if (!pen) throw new Error('Canvas is unavailable')
  pen.imageSmoothingQuality = 'high'
  pen.translate(canvas.width / 2, canvas.height / 2)
  pen.rotate(((q * 90 + rest) * Math.PI) / 180)
  pen.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2)
  return { source: canvas, width: canvas.width, height: canvas.height }
}
